<template>
  <preloader-overlay :loading="loading">
    <div :class="$style.container">

      <div :class="$style.header">
        <responsive-controller>
          <template #tablet>
            <screen-header
              :title="$t('choose.card.for.payout')"
              :history-mode="false"
              @navigate="handleNavigate"
            />
          </template>
          <template #default>
            <div>
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="handleNavigate"
              />
              <screen-title
                :title="$t('card.binding.title')"
              />
            </div>
          </template>
        </responsive-controller>
      </div>

      <div :class="$style.content">
        <selectable-card-panel
          v-for="(card, index) in sortedData"
          :key="index"
          v-m-b="20"
          :card="card"
          @click="handleChangeCard(card)"
        >
          <template
            v-if="getLimitByCard(card)"
            v-slot:additional
          >
            <payout-limits
              :currency="getCurrency"
              :limits="getLimitByCard(card)"
              :loading="loading"
            />
          </template>
        </selectable-card-panel>

        <adding-new-panel
          :message="$t('bind.new.card')"
          @click="$router.push({ name: 'card.binding' })"
        />
      </div>

    </div>
  </preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import moment from 'lib/utils/date';

import HTTPService from '@/services/Http';

import ScreenTitle from 'molecules/screen/ScreenTitle';
import ScreenHeader from 'molecules/screen/ScreenHeader';
import NavigateButton from 'molecules/common/NavigateButton';
import PreloaderOverlay from 'molecules/common/PreloaderOverlay';
import AddingNewPanel from 'molecules/panels/AddingNewPanel';

import PayoutLimits from 'organisms/payout/components/PayoutLimits';
import SelectableCardPanel from 'organisms/card/SelectableCardPanel';

const HIGH_ORDER = 1;
const MIDDLE_ORDER = 2;
const LOW_ORDER = 3;

export default {
  name: 'CardSelect',
  components: {
    ScreenTitle,
    ScreenHeader,
    NavigateButton,
    PreloaderOverlay,
    AddingNewPanel,
    PayoutLimits,
    SelectableCardPanel,
  },
  created() {
    if (this.isCurrencyCrypto) {
      this.$router.push({ name: 'wallet.select' });
      return;
    }

    this.loadData();
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    sortedData() {
      if (this.isEmptyCardList) {
        return [];
      }

      return this.getCardList
        .map(this.prepareCard)
        .sort(this.sortCard);
    },
    ...mapState('cards', ['cardLimits']),
    ...mapGetters([
      'getCurrency',
      'isCurrencyCrypto',
    ]),
    ...mapGetters('cards', [
      'isEmptyCardList',
      'getCardList',
      'isNoLinkedCards',
      'isLinkedCardIsExpired',
      'isLinkedCardInBlacklisted',
      'linkedCardLimits',
    ]),
  },
  methods: {
    async loadData() {
      this.loading = true;

      if (this.isCurrencyCrypto) {
        this.$router.push({ name: 'wallet.select' });
        return;
      }

      await Promise.all([
        this.fetchCardList(),
        this.fetchCardLimits(),
      ]);

      if (this.isLinkedCardIsExpired) {
        this.$alert({
          type: 'warning',
          text: 'card.expired',
        });
      }

      this.loading = false;
    },
    prepareCard(card) {
      if (card.blacklisted || moment().isAfter(moment(card.expireDt))) {
        card.order = LOW_ORDER;
      } else if (card.default) {
        card.order = HIGH_ORDER;
      } else {
        card.order = MIDDLE_ORDER;
      }
      return card;
    },
    sortCard(cardA, cardB) {
      if (cardA.order > cardB.order) {
        return 1;
      }
      if (cardA.order < cardB.order) {
        return -1;
      }
      return 0;
    },
    handleNavigate() {
      if (this.isEmptyCardList || this.isNoLinkedCards || this.isLinkedCardIsExpired || this.isLinkedCardInBlacklisted) {
        this.$router.push({ name: 'cabinet' });
      } else {
        this.$router.push({ name: 'payout' });
      }
    },
    handleChangeCard(card) {
      const { id: cardId } = card;

      if (this.$validate.isUndefined(cardId)) {
        return;
      }

      HTTPService.changeCard(cardId)
        .then(response => {
          const { data } = response;

          if (data.status === 'CARD_SWITCHED') {
            this.$alert({
              type: 'success',
              text: 'card.for.payout.established',
            });

            this.$router.push({ name: 'payout' });
            return;
          }

          this.$router.push({ name: 'cardSelectedMessage' });
        })
        .catch(() => {
          this.$alert({
            type: 'warning',
            text: 'problems.with.server',
          });
        });
    },
    getLimitByCard(card) {
      return this.cardLimits.find(limit => limit.cardId === card.id) || null;
    },
    ...mapActions('cards', [
      'fetchCardList',
      'fetchCardLimits',
    ]),
  },
};
</script>

<style lang="sass" module>
  .container
    +size(100%, auto)
    max-width: 500px
    margin: 0 auto
    padding: 20px 0 60px

    +responsive('large')
      padding: 60px 0

  .header
    position: relative
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .content
    +base-sub-mount
    margin-bottom: 20px
</style>
